/**
 * Builds sidebar NavSection[] from the unified route manifest.
 *
 * This replaces the hand-maintained FUNCTIONALITY_NAV object in layoutConstants.ts.
 * The manifest is the single source of truth for both routing and navigation.
 */

import type { NavItem, NavSection } from '../components/layout/layoutConstants';
import type { RouteEntry } from './types';
import { MANIFEST } from './manifest';

/**
 * Section ordering per functionality.
 * Sections not listed here appear after all listed sections, in manifest order.
 */
const SECTION_ORDER: Record<string, string[]> = {
  audit_workbook: [
    'Overview',
    'Data Entry',
    'Data Review',
    'Data Provided',
    'Analysis',
    'Audit',
    'Import / Export',
    'Reporting',
    'IFRS 17 Reference',
  ],
  budgeting: ['Overview', 'Budgeting', 'IFRS 17 Reference'],
  actuarial_model: [
    'Overview',
    'Model',
    'Global Assumptions',
    'Company Assumptions',
    'Data',
    'GMM / VFA Model',
    'Results',
    'IFRS 17 Reference',
  ],
  reinsurance: ['Overview', 'Reinsurance', 'IFRS 17 Reference'],
  pricing: [
    'Overview',
    'Active Products',
    'Actuarial Pricing',
    'Life Profit Testing',
    'IFRS 17 Reference',
  ],
  policy_data: ['Overview', 'Data Entry', 'Reports', 'IFRS 17 Reference'],
  financial_condition_report: [
    'Overview',
    'Data Entry',
    'Report Sections',
    'Tools',
    'IFRS 17 Reference',
  ],
};

/**
 * Sections that should be collapsible in the sidebar.
 * Derived from the manifest's navCollapsible flags, but kept as a lookup
 * for sections that are always collapsible regardless of individual entries.
 */
const COLLAPSIBLE_SECTIONS = new Set([
  'Data Entry',
  'Data Review',
  'Data Provided',
  'Analysis',
  'GMM / VFA Model',
]);

/** Convert a RouteEntry to a NavItem. */
function toNavItem(entry: RouteEntry): NavItem {
  const item: NavItem = {
    to: entry.path.startsWith('/') ? entry.path : `/${entry.path}`,
    label: entry.label,
    icon: entry.navIcon ?? 'Dashboard',
  };
  if (entry.navRoles && entry.navRoles.length > 0) {
    item.roles = entry.navRoles;
  }
  if (entry.ifrs4Only) {
    item.ifrs4Only = true;
  }
  return item;
}

/**
 * Build sidebar nav sections for a given functionality from the route manifest.
 *
 * Filters manifest entries where `entry.functionality === functionality`
 * and `!entry.hideFromNav`, groups by `navSection`, and produces the
 * NavSection[] array that SidebarContent expects.
 *
 * Cross-cutting entries (those with no `functionality` but with a matching
 * `navSection` like "IFRS 17 Reference") are appended if they appear in
 * the functionality's section order.
 */
export function buildNavFromManifest(functionality: string): NavSection[] {
  // Collect entries for this functionality
  const entries = MANIFEST.filter(
    (e) =>
      !e.hideFromNav &&
      e.navSection &&
      e.label &&
      (e.functionality === functionality ||
        // Cross-cutting entries (no functionality) that belong to a section
        // listed in this functionality's section order
        (!e.functionality &&
          SECTION_ORDER[functionality]?.includes(e.navSection)))
  );

  // Group by navSection, preserving insertion order within each group
  const groups = new Map<string, RouteEntry[]>();
  for (const entry of entries) {
    const section = entry.navSection!;
    if (!groups.has(section)) {
      groups.set(section, []);
    }
    groups.get(section)!.push(entry);
  }

  // Sort entries within each group by navOrder (if present), then insertion order
  for (const [, items] of groups) {
    items.sort((a, b) => {
      const orderA = a.navOrder ?? 9999;
      const orderB = b.navOrder ?? 9999;
      return orderA - orderB;
    });
  }

  // Build NavSection[] in the prescribed order
  const order = SECTION_ORDER[functionality] ?? [];
  const result: NavSection[] = [];
  const seen = new Set<string>();

  for (const sectionLabel of order) {
    const group = groups.get(sectionLabel);
    if (!group || group.length === 0) continue;
    seen.add(sectionLabel);

    const section: NavSection = {
      label: sectionLabel,
      items: group.map(toNavItem),
    };

    // Mark collapsible if any entry says so or it's in the always-collapsible set
    if (
      COLLAPSIBLE_SECTIONS.has(sectionLabel) ||
      group.some((e) => e.navCollapsible)
    ) {
      section.collapsible = true;
    }

    result.push(section);
  }

  // Append any sections not in the prescribed order
  for (const [sectionLabel, group] of groups) {
    if (seen.has(sectionLabel)) continue;
    const section: NavSection = {
      label: sectionLabel,
      items: group.map(toNavItem),
    };
    if (
      COLLAPSIBLE_SECTIONS.has(sectionLabel) ||
      group.some((e) => e.navCollapsible)
    ) {
      section.collapsible = true;
    }
    result.push(section);
  }

  return result;
}

/**
 * Build all functionality navs at once, keyed by functionality ID.
 * Drop-in replacement for the old FUNCTIONALITY_NAV constant.
 */
export function buildAllNavs(): Record<string, NavSection[]> {
  const functionalities = [
    'audit_workbook',
    'budgeting',
    'actuarial_model',
    'reinsurance',
    'pricing',
    'policy_data',
    'financial_condition_report',
  ];
  const result: Record<string, NavSection[]> = {};
  for (const f of functionalities) {
    result[f] = buildNavFromManifest(f);
  }
  return result;
}
